import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Calendar,
  Users,
  Sparkles,
  TrendingUp,
  Globe,
  Zap,
  CheckCircle,
  Search,
  ShieldCheck,
  Star,
  Clock,
  MapPin,
  Bookmark,
  Share2,
  Quote,
  ChevronDown,
  HelpCircle,
  Mail,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { AnimatePresence } from "../../components/AnimatePresenceWrapper";
import { HomepageNavbar } from "../../components/HomepageNavbar";
import { HomepageFooter } from "../../components/HomepageFooter";

import "./styles.scss";

const HomePage: React.FC = () => {
  const [testimonialPage, setTestimonialPage] = useState(0);
  const [testimonialsPerPage, setTestimonialsPerPage] = useState(3);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const navigate = useNavigate();

  // Add smooth scrolling behavior
  useEffect(() => {
    document.documentElement.style.scrollBehavior = "smooth";
    return () => {
      document.documentElement.style.scrollBehavior = "auto";
    };
  }, []);

  const testimonials = [
    {
      name: "Aisha Okafor",
      role: "Product Manager",
      company: "Lagos, Nigeria",
      initials: "AO",
      quote:
        "I stopped screenshotting event flyers on Instagram. Aiserwave gives me one clean place to actually find and manage the events I care about.",
      rating: 5,
    },
    {
      name: "David Chen",
      role: "Software Engineer",
      company: "Remote",
      initials: "DC",
      quote:
        "Tickets, receipts, calendar invites—everything just works. It feels like the platform actually respects my time.",
      rating: 5,
    },
    {
      name: "Zara Ali",
      role: "Community Lead",
      company: "Tech Collective",
      initials: "ZA",
      quote:
        "Our monthly meetups doubled in attendance after moving to Aiserwave. People discover us faster and show up more consistently.",
      rating: 5,
    },
    {
      name: "Michael Johnson",
      role: "Creative Director",
      company: "Studio Eleven",
      initials: "MJ",
      quote:
        "The check‑in flow is so smooth that we stopped printing badges entirely. Attendees actually compliment our registration desk now.",
      rating: 5,
    },
    {
      name: "Adaeze N.",
      role: "Event Producer",
      company: "Culture Lab",
      initials: "AN",
      quote:
        "Aiserwave makes it easy to mix free community events with premium experiences without confusing our audience.",
      rating: 5,
    },
    {
      name: "Ravi Patel",
      role: "Startup Founder",
      company: "Growth Loop",
      initials: "RP",
      quote:
        "I find at least one high‑signal event every week. It&apos;s become part of my routine, like checking my calendar.",
      rating: 5,
    },
    {
      name: "Ifunanya E.",
      role: "UX Designer",
      company: "Freelance",
      initials: "IE",
      quote:
        "The curation feels intentional. I&apos;m not scrolling through noise—I&apos;m browsing things I&apos;d actually go to.",
      rating: 5,
    },
    {
      name: "Sara Müller",
      role: "Marketing Lead",
      company: "SaaS Collective",
      initials: "SM",
      quote:
        "We launched a new series of breakfast events and sold out three editions in a row. The analytics help us plan smarter.",
      rating: 5,
    },
    {
      name: "Kwame Mensah",
      role: "Community Builder",
      company: "Creators Hub",
      initials: "KM",
      quote:
        "People discover us through Aiserwave first, then stay for the community. It&apos;s become our default home for events.",
      rating: 5,
    },
  ];

  // Adjust testimonials per slide based on screen size
  useEffect(() => {
    const updatePerPage = () => {
      const width = window.innerWidth;
      if (width < 640) {
        setTestimonialsPerPage(1);
      } else if (width < 1024) {
        setTestimonialsPerPage(2);
      } else {
        setTestimonialsPerPage(3);
      }
    };

    updatePerPage();
    window.addEventListener("resize", updatePerPage);
    return () => window.removeEventListener("resize", updatePerPage);
  }, []);

  const totalTestimonialPages = Math.ceil(
    testimonials.length / testimonialsPerPage
  );

  // Auto-advance testimonials
  useEffect(() => {
    if (totalTestimonialPages <= 1) return;

    const id = window.setInterval(() => {
      setTestimonialPage((prev) => (prev + 1) % totalTestimonialPages);
    }, 7000);

    return () => window.clearInterval(id);
  }, [totalTestimonialPages]);

  const startIndex = testimonialPage * testimonialsPerPage;
  const visibleTestimonials = testimonials.slice(
    startIndex,
    startIndex + testimonialsPerPage
  );

  const faqs = [
    {
      question: "What is Aiserwave?",
      answer:
        "Aiserwave is a modern platform for discovering, booking, and managing events—from intimate meetups to large conferences—across cities and online.",
    },
    {
      question: "Do I need an account to get tickets?",
      answer:
        "You can grab tickets as a guest for most events, but creating a free account lets you keep all your tickets, receipts, and past events in one place.",
    },
    {
      question: "How will I receive my ticket?",
      answer:
        "Tickets are sent instantly via email and also saved to your Aiserwave account. On event day, just show your QR code on your phone—no printing required.",
    },
    {
      question: "What happens if an event is rescheduled or cancelled?",
      answer:
        "Organizers can message all attendees directly through Aiserwave. You’ll get clear updates by email, including any new dates or refund instructions.",
    },
    {
      question: "Is my payment information secure?",
      answer:
        "All payments are processed through trusted gateways with industry‑standard encryption. Aiserwave never stores your full card details on our servers.",
    },
  ];

  return (
    <>
      <Helmet>
        <title>
          Aiserwave - Adaptable. Intelligent. Scalable. Enterprise Ready. |
          Event Management Software
        </title>
        <meta
          name="description"
          content="Aiserwave is the event technology system built for professional event organisers, enterprise teams, and global brands. The platform supports ticketing, registration, payments, check in, analytics, and audience engagement in one place."
        />
        <meta
          name="keywords"
          content="event management software, enterprise event platform, scalable event system, intelligent event technology, multi tenant event SaaS, event automation, real time attendance tracking, secure event software, enterprise grade ticketing"
        />
        <meta
          property="og:title"
          content="Aiserwave - Adaptable. Intelligent. Scalable. Enterprise Ready."
        />
        <meta
          property="og:description"
          content="Aiserwave is the event technology system built for professional event organisers, enterprise teams, and global brands."
        />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://aiserwave.com/" />
      </Helmet>
      <div className="home-page-container min-h-screen bg-white">
        <HomepageNavbar />

        {/* Hero Section */}
        <section className="pt-32 pb-32 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
          {/* Background Elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {/* Grid Pattern */}
            <div
              className="absolute inset-0 opacity-[0.02]"
              style={{
                backgroundImage: `linear-gradient(#17265a 1px, transparent 1px), linear-gradient(90deg, #17265a 1px, transparent 1px)`,
                backgroundSize: "40px 40px",
              }}
            ></div>

            {/* Floating Shapes */}
            <motion.div
              animate={{
                y: [0, -20, 0],
                rotate: [0, 5, 0],
              }}
              transition={{
                duration: 6,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="absolute top-20 left-10 w-32 h-32 bg-[#17265a]/5"
            ></motion.div>

            <motion.div
              animate={{
                y: [0, 20, 0],
                rotate: [0, -5, 0],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 1,
              }}
              className="absolute top-40 right-20 w-24 h-24 bg-[#17265a]/5"
            ></motion.div>

            <motion.div
              animate={{
                y: [0, -15, 0],
              }}
              transition={{
                duration: 7,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 2,
              }}
              className="absolute bottom-20 left-1/4 w-20 h-20 bg-[#17265a]/5"
            ></motion.div>
          </div>

          <div className="max-w-7xl mx-auto relative z-10">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              {/* Left Column - Content */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="space-y-10"
              >
                {/* Badge */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="inline-flex items-center gap-2.5 bg-[#17265a]/5 px-5 py-2.5 border border-[#17265a]/10 hover:bg-[#17265a]/10 transition-colors cursor-pointer"
                >
                  <Sparkles className="w-4 h-4 text-[#17265a]" />
                  <span className="text-sm font-semibold text-[#17265a]">
                    Discover Amazing Events
                  </span>
                  <motion.div
                    animate={{ x: [0, 4, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <ArrowRight className="w-3.5 h-3.5 text-[#17265a]" />
                  </motion.div>
                </motion.div>

                {/* Heading */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  className="space-y-4"
                >
                  <h1 className="text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold leading-[1.1] tracking-tight">
                    <span className="text-gray-900">Aiserwave.</span>
                    <br />
                    <span className="text-[#17265a] relative inline-block">
                      Adaptable. Intelligent. Scalable. Enterprise Ready.
                      <motion.span
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ duration: 0.8, delay: 1 }}
                        className="absolute bottom-0 left-0 right-0 h-3 bg-[#17265a]/10 origin-left"
                      ></motion.span>
                    </span>
                  </h1>
                </motion.div>

                {/* Description */}
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  className="text-xl md:text-2xl text-gray-600 leading-relaxed max-w-2xl"
                >
                  Aiserwave is the event technology system built for
                  professional event organisers, enterprise teams, and global
                  brands. The platform supports ticketing, registration,
                  payments, check in, analytics, and audience engagement in one
                  place.
                </motion.p>

                {/* Features List */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.5 }}
                  className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4"
                >
                  {[
                    { icon: Zap, text: "Lightning Fast Setup" },
                    { icon: Globe, text: "Global Reach" },
                    { icon: TrendingUp, text: "Real-time Analytics" },
                    { icon: CheckCircle, text: "Easy Management" },
                  ].map((feature, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                      className="flex items-center gap-3"
                    >
                      <div className="w-10 h-10 bg-[#17265a]/10 flex items-center justify-center flex-shrink-0">
                        <feature.icon className="w-5 h-5 text-[#17265a]" />
                      </div>
                      <span className="text-gray-700 font-medium">
                        {feature.text}
                      </span>
                    </motion.div>
                  ))}
                </motion.div>

                {/* CTA Buttons */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.7 }}
                  className="flex flex-wrap gap-4 pt-4"
                >
                  <motion.button
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => navigate("/pricing")}
                    className="bg-[#17265a] text-white px-8 py-4.5 hover:bg-[#0f1a3f] transition-all duration-300 flex items-center gap-2.5 shadow-md hover:shadow-lg font-semibold text-lg group"
                  >
                    <span>Start with Aiserwave</span>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => navigate("#events")}
                    className="bg-transparent border-2 border-[#17265a] text-[#17265a] px-8 py-4.5 hover:bg-[#17265a]/5 transition-all duration-300 flex items-center gap-2.5 font-semibold text-lg group"
                  >
                    <span>Book a walkthrough</span>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </motion.button>
                </motion.div>
              </motion.div>

              {/* Right Column - Visual */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="relative"
              >
                {/* Decorative Background */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#17265a]/5 via-transparent to-[#17265a]/5"></div>
                <div className="absolute -inset-4 bg-gradient-to-r from-[#17265a]/5 to-transparent blur-3xl"></div>

                {/* Main Visual Grid */}
                <div className="relative z-10 space-y-4">
                  {/* Top Row */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* Card 1 - Large */}
                    <motion.div
                      initial={{ opacity: 0, y: 30, rotate: -2 }}
                      animate={{ opacity: 1, y: 0, rotate: 0 }}
                      transition={{ duration: 0.6, delay: 0.5 }}
                      whileHover={{ y: -8, rotate: 1 }}
                      className="bg-white border-2 border-gray-200 p-6 shadow-xl hover:shadow-2xl transition-all duration-300 group cursor-pointer"
                    >
                      <div className="space-y-4">
                        <div className="w-14 h-14 bg-[#17265a] flex items-center justify-center group-hover:scale-110 transition-transform">
                          <Calendar className="w-7 h-7 text-white" />
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900 text-xl mb-2">
                            Tech Summit 2025
                          </h3>
                          <p className="text-sm text-gray-600 leading-relaxed">
                            Join industry leaders for cutting-edge insights and
                            unparalleled networking opportunities
                          </p>
                        </div>
                        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                          <div className="flex items-center gap-2 text-sm text-[#17265a] font-semibold">
                            Learn More
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                          </div>
                          <div className="text-xs text-gray-500 font-medium">
                            Dec 15
                          </div>
                        </div>
                      </div>
                    </motion.div>

                    {/* Card 2 - Primary */}
                    <motion.div
                      initial={{ opacity: 0, y: 30, rotate: 2 }}
                      animate={{ opacity: 1, y: 0, rotate: 0 }}
                      transition={{ duration: 0.6, delay: 0.6 }}
                      whileHover={{ y: -8, rotate: -1 }}
                      className="bg-[#17265a] p-6 shadow-xl hover:shadow-2xl transition-all duration-300 text-white group cursor-pointer"
                    >
                      <div className="space-y-4">
                        <div className="w-14 h-14 bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform">
                          <Users className="w-7 h-7 text-white" />
                        </div>
                        <div>
                          <h3 className="font-bold text-lg mb-2">
                            Networking Night
                          </h3>
                          <p className="text-sm text-white/90 leading-relaxed">
                            Connect with professionals and expand your network
                          </p>
                        </div>
                        <div className="flex items-center gap-3 pt-2 border-t border-white/10">
                          <div className="flex -space-x-2">
                            {[1, 2, 3].map((i) => (
                              <div
                                key={i}
                                className="w-8 h-8 bg-white/20 border-2 border-white rounded-full"
                              ></div>
                            ))}
                          </div>
                          <span className="text-xs text-white/80">
                            500+ attending
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  </div>

                  {/* Middle Card - Full Width */}
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.7 }}
                    whileHover={{ y: -8 }}
                    className="bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 p-6 shadow-xl hover:shadow-2xl transition-all duration-300 group cursor-pointer"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-5">
                        <div className="w-20 h-20 bg-gradient-to-br from-[#17265a] to-[#2d3f7a] flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                          <Sparkles className="w-10 h-10 text-white" />
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900 text-2xl mb-1">
                            Workshop Series
                          </h3>
                          <p className="text-gray-600 font-medium">
                            Hands-on learning experiences that transform careers
                          </p>
                          <div className="flex items-center gap-4 mt-3">
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <Calendar className="w-4 h-4" />
                              <span>8 Sessions</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <Users className="w-4 h-4" />
                              <span>Expert Instructors</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-4xl font-bold text-[#17265a] mb-1">
                          1,234
                        </div>
                        <div className="text-sm text-gray-600 font-semibold">
                          Attendees
                        </div>
                        <div className="w-24 h-2 bg-gray-200 mt-3">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: "85%" }}
                            transition={{ duration: 1, delay: 1 }}
                            className="h-full bg-[#17265a]"
                          ></motion.div>
                        </div>
                      </div>
                    </div>
                  </motion.div>

                  {/* Bottom Row - Stats Cards */}
                  <div className="grid grid-cols-3 gap-4">
                    {[
                      { icon: TrendingUp, value: "95%", label: "Success" },
                      { icon: Globe, value: "24/7", label: "Support" },
                      { icon: Zap, value: "<1s", label: "Speed" },
                    ].map((item, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5, delay: 0.8 + index * 0.1 }}
                        whileHover={{ y: -4 }}
                        className="bg-white border border-gray-200 p-4 shadow-md hover:shadow-lg transition-all duration-300 text-center"
                      >
                        <div className="w-10 h-10 bg-[#17265a]/10 flex items-center justify-center mx-auto mb-3">
                          <item.icon className="w-5 h-5 text-[#17265a]" />
                        </div>
                        <div className="text-2xl font-bold text-gray-900 mb-1">
                          {item.value}
                        </div>
                        <div className="text-xs text-gray-600 font-medium">
                          {item.label}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Core Benefits Section */}
        <section
          id="benefits"
          className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50 border-t border-gray-100"
        >
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Built for events that demand structure and precision
              </h2>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="grid gap-6 md:grid-cols-2 lg:grid-cols-3"
            >
              {[
                "Manage large attendee numbers without system slowdown",
                "Run multiple events under one account",
                "Accept global payments",
                "Track real time attendance",
                "View performance analytics",
                "Keep a secure and reliable environment",
                "Integrate with your internal systems",
              ].map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#17265a] mt-0.5 flex-shrink-0" />
                    <p className="text-gray-700 font-medium">{benefit}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Why Aiserwave Section */}
        <section
          id="why"
          className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-t border-gray-100"
        >
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Why teams choose Aiserwave
              </h2>
              <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Teams select Aiserwave because the system responds to growth. It
                adapts to different event sizes. It supports simple events and
                large conferences. It provides a clear interface. It delivers
                consistent performance. It removes friction from planning and
                execution.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Platform Modules Section */}
        <section
          id="modules"
          className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50 border-t border-gray-100"
        >
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                One platform. Many modules.
              </h2>
            </motion.div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {[
                {
                  name: "Aiserwave Core",
                  description:
                    "Manages accounts, access, links, and system settings.",
                  link: "/modules/core",
                },
                {
                  name: "Aiserwave Studio",
                  description: "Builds event pages, programmes, and layouts.",
                  link: "/modules/studio",
                },
                {
                  name: "Aiserwave Tickets",
                  description:
                    "Handles registrations, payments, ticket classes, and check in.",
                  link: "/modules/tickets",
                },
                {
                  name: "Aiserwave Analytics",
                  description:
                    "Shows attendance data, sales performance, and user activity.",
                  link: "/modules/analytics",
                },
                {
                  name: "Aiserwave Workspace",
                  description:
                    "Supports team roles, approvals, and collaboration.",
                  link: "/modules/workspace",
                },
                {
                  name: "Aiserwave Community",
                  description:
                    "Engages attendees through profile pages and updates.",
                  link: "/modules/community",
                },
                {
                  name: "Aiserwave API",
                  description:
                    "Connects Aiserwave to internal systems and third party tools.",
                  link: "/modules/api",
                },
              ].map((module, index) => (
                <motion.div
                  key={module.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  onClick={() => navigate(module.link)}
                  className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all cursor-pointer group"
                >
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#17265a] transition-colors">
                    {module.name}
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    {module.description}
                  </p>
                  <div className="flex items-center gap-2 text-[#17265a] font-semibold text-sm">
                    <span>Learn more</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Social Proof Section */}
        <section
          id="social-proof"
          className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-t border-gray-100"
        >
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Built for professionals
              </h2>
              <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Aiserwave supports organisers, enterprises, consultants, and
                creative teams who want a reliable platform for events. They
                trust the system for its accuracy, speed, and structure.
              </p>
            </motion.div>
          </div>
        </section>

        {/* How it works */}
        <section
          id="features"
          className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50 border-t border-gray-100"
        >
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <p className="inline-flex items-center gap-2 px-4 py-1 rounded-full bg-[#17265a]/5 text-sm font-semibold text-[#17265a] mb-4">
                <Zap className="w-4 h-4" />
                Built for effortless event discovery
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                How Aiserwave works
              </h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                From browsing to check-in, we keep every step smooth so you can
                focus on enjoying the experience.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="grid gap-8 md:grid-cols-4"
            >
              {[
                {
                  icon: Search,
                  title: "Discover events",
                  description:
                    "Search by city, date, or interest and instantly see curated events that match your vibe.",
                },
                {
                  icon: Calendar,
                  title: "Secure your spot",
                  description:
                    "Book tickets in a few taps with secure payments and instant confirmation.",
                },
                {
                  icon: ShieldCheck,
                  title: "Stay organized",
                  description:
                    "Keep all your tickets, receipts, and event details in one place—on any device.",
                },
                {
                  icon: Users,
                  title: "Show up ready",
                  description:
                    "Receive reminders, updates, and check in with a single QR code when you arrive.",
                },
              ].map((step, index) => {
                const Icon = step.icon;
                return (
                  <motion.div
                    key={step.title}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.5, delay: 0.15 * index }}
                    className="bg-white border border-gray-100 shadow-sm hover:shadow-md rounded-2xl p-6 flex flex-col gap-4"
                  >
                    <div className="w-11 h-11 rounded-xl bg-[#17265a]/10 flex items-center justify-center text-[#17265a]">
                      <Icon className="w-6 h-6" />
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-semibold text-lg text-gray-900">
                        {step.title}
                      </h3>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                    <div className="mt-auto text-xs font-semibold text-gray-400">
                      Step {index + 1}
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </section>

        {/* Featured events */}
        <section
          id="events"
          className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-t border-gray-100"
        >
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                  Explore featured events
                </h2>
                <p className="text-gray-600 max-w-xl">
                  Handpicked experiences happening near you and online—from
                  intimate gatherings to large‑scale festivals.
                </p>
              </div>
              <motion.button
                whileHover={{ scale: 1.03, y: -1 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => navigate("#")}
                className="inline-flex items-center gap-2 rounded-full border border-gray-300 px-5 py-2.5 text-sm font-semibold text-gray-800 hover:border-[#17265a] hover:text-[#17265a] hover:bg-[#17265a]/5 transition-colors"
              >
                <Search className="w-4 h-4" />
                Browse all events
              </motion.button>
            </div>

            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {[
                {
                  title: "The Community Table",
                  image:
                    "https://images.unsplash.com/photo-1514525253161-7a46d19cd819?auto=format&fit=crop&w=1200&q=80",
                  schedule: "Every Thu · 7PM",
                  location: "Nearby with your location",
                  description:
                    "An intimate dinner where you're matched with new people based on shared interests and personality.",
                  goingText: "30+ going",
                },
                {
                  title: "Founder Fireside Circle",
                  image:
                    "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=1200&q=80",
                  schedule: "Tues · 6:30PM",
                  location: "Downtown, Lagos",
                  description:
                    "A small‑group conversation with operators and founders sharing real stories, wins, and hard lessons.",
                  goingText: "45 founders attending",
                },
                {
                  title: "Late Night Product Lab",
                  image:
                    "https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&w=1200&q=80",
                  schedule: "Fri · 8PM",
                  location: "Online · interactive",
                  description:
                    "Ship something you're proud of in one night with guided prompts, breakout rooms, and live feedback.",
                  goingText: "120 builders live",
                },
              ].map((event, index) => (
                <motion.div
                  key={event.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.45, delay: index * 0.06 }}
                  className="group flex flex-col overflow-hidden rounded-[1.75rem] bg-white shadow-sm hover:shadow-xl hover:-translate-y-1.5 transition-all cursor-pointer border border-gray-100"
                >
                  {/* Image */}
                  <div className="relative h-52 w-full overflow-hidden">
                    <div
                      className="h-full w-full bg-cover bg-center"
                      style={{
                        backgroundImage: `linear-gradient(to top, rgba(15,23,42,0.45), rgba(15,23,42,0.1)), url(${event.image})`,
                      }}
                    />
                    <div className="absolute inset-x-4 top-4 flex justify-end gap-3">
                      <button className="flex h-9 w-9 items-center justify-center rounded-full bg-white/80 text-slate-800 shadow-sm backdrop-blur hover:bg-white">
                        <Bookmark className="w-4 h-4" />
                      </button>
                      <button className="flex h-9 w-9 items-center justify-center rounded-full bg-white/80 text-slate-800 shadow-sm backdrop-blur hover:bg-white">
                        <Share2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="px-5 pt-5 pb-4 md:px-6 md:pt-5 md:pb-5 flex flex-col gap-3">
                    <h3 className="text-xl font-semibold text-gray-900 tracking-tight">
                      {event.title}
                    </h3>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                      <span className="inline-flex items-center gap-1.5">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        {event.schedule}
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <MapPin className="w-4 h-4 text-gray-500" />
                        {event.location}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed line-clamp-2">
                      {event.description}
                    </p>
                    <div className="mt-1 flex items-center justify-between gap-3">
                      <div className="flex -space-x-2">
                        {[0, 1, 2].map((i) => (
                          <div
                            key={i}
                            className="h-8 w-8 rounded-full border-2 border-white bg-gradient-to-tr from-[#17265a] to-[#4f46e5]"
                          />
                        ))}
                      </div>
                      <span className="text-sm font-medium text-gray-900">
                        {event.goingText}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* About & social proof */}
        <section
          id="about"
          className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50 border-t border-gray-100"
        >
          <div className="max-w-7xl mx-auto grid gap-12 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)] items-start">
            {/* About copy */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <p className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#17265a]/5 text-[#17265a] text-xs font-semibold">
                <Star className="w-4 h-4" />
                Trusted event experiences
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Not just tickets. A better way to experience events.
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                Aiserwave helps you go from &quot;What should I do?&quot; to
                &quot;I can&apos;t believe I almost missed this&quot;—with
                curated events, clear details, and a checkout flow that feels
                effortless.
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                {[
                  {
                    title: "Transparent pricing",
                    description:
                      "See fees and ticket options upfront so you always know what you&apos;re paying for.",
                  },
                  {
                    title: "Real-time updates",
                    description:
                      "Get notified instantly if schedules change or new ticket tiers open up.",
                  },
                  {
                    title: "Mobile-first",
                    description:
                      "Access tickets, maps, and event info from any device with a clean, fast interface.",
                  },
                  {
                    title: "Built for every city",
                    description:
                      "From global conferences to neighborhood pop-ups, Aiserwave scales with your plans.",
                  },
                ].map((item) => (
                  <div key={item.title} className="flex gap-3">
                    <div className="mt-1">
                      <CheckCircle className="w-4 h-4 text-[#17265a]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {item.title}
                      </h3>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Testimonials / FAQ */}
            <div className="space-y-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="rounded-2xl bg-white border border-gray-100 shadow-sm p-6 space-y-4"
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-[#17265a]">
                      Attendee stories
                    </p>
                    <p className="text-sm text-gray-500">
                      What people are saying about events on Aiserwave.
                    </p>
                  </div>
                  <div className="flex -space-x-2">
                    {[0, 1, 2].map((i) => (
                      <div
                        key={i}
                        className="w-9 h-9 rounded-full border-2 border-white bg-[#17265a]/10"
                      />
                    ))}
                  </div>
                </div>
                {[
                  {
                    quote:
                      "Finding quality tech events used to be a chore. Now I have a full calendar of meetups and conferences that actually matter to my career.",
                    name: "Tomi, Product Designer",
                  },
                  {
                    quote:
                      "I booked a weekend festival and three workshops in under five minutes. The check‑in experience was smoother than any event platform I&apos;ve used.",
                    name: "Ada, Creative Director",
                  },
                ].map((testimonial) => (
                  <div
                    key={testimonial.name}
                    className="border-t border-gray-100 pt-4 space-y-2"
                  >
                    <p className="text-sm text-gray-800 leading-relaxed">
                      “{testimonial.quote}”
                    </p>
                    <p className="text-xs font-semibold text-gray-500">
                      {testimonial.name}
                    </p>
                  </div>
                ))}
                <div className="flex items-center gap-2 pt-2 text-xs text-gray-500">
                  <Star className="w-4 h-4 text-yellow-400" />
                  <span>4.9 / 5 average rating from event attendees</span>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: 0.15 }}
                className="rounded-2xl bg-[#17265a] text-white p-6 space-y-4"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-white/70">
                      Have questions?
                    </p>
                    <h3 className="text-lg font-semibold mb-2">
                      A quick guide for first‑time attendees
                    </h3>
                    <ul className="space-y-2 text-sm text-white/90">
                      <li>
                        • You&apos;ll receive tickets and receipts instantly via
                        email after checkout.
                      </li>
                      <li>
                        • Most events support mobile check‑in—no printing
                        required.
                      </li>
                      <li>
                        • If plans change, organizers can send updates directly
                        to your inbox.
                      </li>
                    </ul>
                  </div>
                </div>
                <motion.button
                  whileHover={{ scale: 1.03, y: -1 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => navigate("#events")}
                  className="inline-flex items-center gap-2 rounded-full bg-white text-[#17265a] px-5 py-2.5 text-sm font-semibold shadow-sm hover:shadow-md transition-all"
                >
                  Explore upcoming events
                  <ArrowRight className="w-4 h-4" />
                </motion.button>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Testimonials slider */}
        <section
          id="testimonials"
          className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-t border-gray-100"
        >
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-3xl mx-auto"
            >
              <p className="inline-flex items-center gap-2 px-4 py-1 rounded-full bg-[#17265a]/5 text-sm font-semibold text-[#17265a] mb-4">
                <Star className="w-4 h-4" />
                Loved by event‑goers everywhere
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                What attendees are saying
              </h2>
              <p className="text-gray-600 text-lg">
                Real stories from people discovering conferences, dinners, and
                festivals through Aiserwave.
              </p>
            </motion.div>

            <div className="mt-10">
              <AnimatePresence mode="wait">
                <motion.div
                  key={testimonialPage}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.45, ease: "easeOut" }}
                  className="grid gap-6 md:gap-8 md:grid-cols-2 xl:grid-cols-3"
                >
                  {visibleTestimonials.map((t, index) => {
                    const palette = [
                      "from-[#eef2ff] to-white border-[#e0e7ff]",
                      "from-[#ecfeff] to-white border-[#bae6fd]",
                      "from-[#ecfdf3] to-white border-[#bbf7d0]",
                    ];
                    const cardClasses = palette[index % palette.length];

                    return (
                      <article
                        key={t.name}
                        className={`relative h-full rounded-2xl border bg-gradient-to-br ${cardClasses} p-6 md:p-7 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all`}
                      >
                        <div className="mb-5 flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/80 shadow-sm">
                            <Quote className="w-4 h-4 text-slate-400" />
                          </div>
                        </div>
                        <p className="mb-6 text-sm md:text-[0.97rem] text-slate-800 leading-relaxed">
                          {t.quote}
                        </p>
                        <div className="space-y-1">
                          <p className="text-sm font-semibold text-slate-900">
                            – {t.name}
                          </p>
                          <p className="text-xs text-slate-500">{t.role}</p>
                        </div>
                      </article>
                    );
                  })}
                </motion.div>
              </AnimatePresence>

              <div className="mt-8 flex items-center justify-center gap-2">
                {Array.from({ length: totalTestimonialPages }).map(
                  (_, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setTestimonialPage(index)}
                      className={`h-2.5 rounded-full transition-all ${
                        testimonialPage === index
                          ? "w-6 bg-[#17265a]"
                          : "w-2.5 bg-gray-300 hover:bg-gray-400"
                      }`}
                      aria-label={`Go to testimonials slide ${index + 1}`}
                    />
                  )
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Newsletter */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-[#0f172a] via-[#111827] to-[#020617] text-white">
          <div className="max-w-7xl mx-auto">
            <div className="grid gap-10 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] items-center">
              <div className="space-y-6">
                <p className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-xs font-semibold tracking-wide text-slate-100">
                  <Mail className="w-4 h-4" />
                  Stay in the loop
                </p>
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
                  Be the first to know about new events and drops.
                </h2>
                <p className="text-sm md:text-base text-slate-200 max-w-xl">
                  Get curated updates on must‑attend conferences, community
                  dinners, and last‑minute tickets—no spam, just the good stuff.
                </p>
                <form
                  onSubmit={(e) => e.preventDefault()}
                  className="mt-4 flex flex-col sm:flex-row gap-3 sm:items-center"
                >
                  <div className="relative flex-1">
                    <input
                      type="email"
                      required
                      placeholder="Enter your email"
                      className="w-full rounded-full border border-white/10 bg-white/5 px-4 py-3.5 pr-32 text-sm text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#38bdf8] focus:border-transparent"
                    />
                    <div className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-[0.7rem] uppercase tracking-[0.12em] text-slate-400">
                      Newsletter
                    </div>
                  </div>
                  <button
                    type="submit"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-[#38bdf8] px-6 py-3.5 text-sm font-semibold text-slate-900 shadow-[0_18px_40px_rgba(56,189,248,0.45)] hover:bg-[#0ea5e9] transition-colors whitespace-nowrap"
                  >
                    Subscribe
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </form>
                <p className="text-[0.75rem] text-slate-400">
                  By subscribing you agree to receive emails from Aiserwave. No
                  noise, unsubscribe anytime.
                </p>
              </div>

              <div className="relative hidden md:block">
                <div className="absolute -inset-10 bg-gradient-to-tr from-[#38bdf8]/20 via-transparent to-[#22c55e]/25 blur-3xl" />
                <div className="relative space-y-4">
                  <div className="rounded-2xl border border-white/10 bg-white/5 px-5 py-4 text-sm text-slate-100 backdrop-blur-sm">
                    <p className="mb-2 font-semibold text-slate-50">
                      Weekly highlight
                    </p>
                    <p className="text-xs text-slate-200">
                      &ldquo;I stopped missing the good stuff in my city—my
                      inbox feels like a curated list of experiences now.&rdquo;
                    </p>
                    <p className="mt-2 text-[0.7rem] text-slate-400">– Amina</p>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-1 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-xs text-slate-100 backdrop-blur-sm">
                      <p className="font-semibold text-slate-50 mb-1">
                        Curated for you
                      </p>
                      <p className="text-[0.7rem] text-slate-300">
                        Hand‑picked events based on what people like you attend.
                      </p>
                    </div>
                    <div className="flex flex-col justify-between rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-xs text-slate-100 backdrop-blur-sm">
                      <p className="font-semibold text-slate-50 mb-1">
                        Zero spam
                      </p>
                      <p className="text-[0.7rem] text-slate-300">
                        1–2 emails a week. That&apos;s it.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section
          id="faq"
          className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50 border-t border-gray-100"
        >
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
              className="max-w-2xl mb-10"
            >
              <p className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#17265a]/5 text-[#17265a] text-xs font-semibold mb-4">
                <HelpCircle className="w-4 h-4" />
                Quick answers
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                Frequently asked questions
              </h2>
              <p className="text-gray-600 text-base md:text-lg">
                Everything you need to know about discovering and attending
                events with Aiserwave.
              </p>
            </motion.div>

            <div className="grid gap-8 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)] items-start">
              <div className="space-y-3">
                {faqs.map((faq, index) => {
                  const isOpen = openFaqIndex === index;
                  return (
                    <div
                      key={faq.question}
                      className="rounded-xl border border-gray-200 bg-white/80 shadow-sm"
                    >
                      <button
                        type="button"
                        onClick={() => setOpenFaqIndex(isOpen ? null : index)}
                        className="w-full flex items-center justify-between gap-4 px-5 py-4 md:px-6 md:py-5 text-left"
                      >
                        <span className="font-medium text-gray-900">
                          {faq.question}
                        </span>
                        <span
                          className={`flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 transition-transform ${
                            isOpen ? "rotate-180" : ""
                          }`}
                        >
                          <ChevronDown className="w-4 h-4" />
                        </span>
                      </button>
                      <AnimatePresence initial={false}>
                        {isOpen && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.25, ease: "easeOut" }}
                          >
                            <div className="px-5 pb-4 md:px-6 md:pb-5 text-sm md:text-[0.95rem] text-gray-600 leading-relaxed border-t border-gray-100">
                              {faq.answer}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 lg:mt-0 space-y-4 text-sm text-gray-600">
                <div className="rounded-2xl bg-white border border-gray-200 p-5 md:p-6 shadow-sm">
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">
                    Still need help?
                  </h3>
                  <p className="mb-3">
                    If you can&apos;t find the answer you&apos;re looking for,
                    we&apos;re a message away.
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate("#")}
                    className="inline-flex items-center gap-2 rounded-full border border-gray-300 px-4 py-2 text-xs font-semibold text-gray-800 hover:border-[#17265a] hover:text-[#17265a] hover:bg-[#17265a]/5 transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    Contact support
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <HomepageFooter />
      </div>
    </>
  );
};

export default HomePage;
